import { motion } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const TIER_CONFIG = {
  platinum: {
    bg: 'linear-gradient(135deg, #1e293b 0%, #334155 30%, #94a3b8 55%, #475569 80%, #1e293b 100%)',
    boxShadow: '0 0 0 2px rgba(148,163,184,0.4), 0 0 20px rgba(148,163,184,0.3), 0 0 40px rgba(100,116,139,0.15)',
    shimmerColor: 'rgba(226,232,240,0.7)',
    pulseColor: 'rgba(148,163,184,0.5)',
    nameFrom: '#cbd5e1',
    nameTo: '#f1f5f9',
    hasPulse: true,
  },
  diamond: {
    bg: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 35%, #2563eb 55%, #0ea5e9 75%, #0f172a 100%)',
    boxShadow: '0 0 0 2px rgba(59,130,246,0.5), 0 0 20px rgba(59,130,246,0.45), 0 0 45px rgba(14,165,233,0.2)',
    shimmerColor: 'rgba(147,197,253,0.8)',
    pulseColor: 'rgba(59,130,246,0.6)',
    nameFrom: '#93c5fd',
    nameTo: '#a5f3fc',
    hasPulse: true,
  },
  gold: {
    bg: 'linear-gradient(135deg, #451a03 0%, #92400e 30%, #d97706 52%, #fbbf24 68%, #78350f 100%)',
    boxShadow: '0 0 0 2px rgba(245,158,11,0.5), 0 0 18px rgba(245,158,11,0.4), 0 0 35px rgba(251,191,36,0.2)',
    shimmerColor: 'rgba(254,243,199,0.8)',
    pulseColor: null,
    nameFrom: '#fcd34d',
    nameTo: '#fde68a',
    hasPulse: false,
  },
  bronze: {
    bg: 'linear-gradient(135deg, #431407 0%, #7c2d12 30%, #b45309 55%, #c2410c 70%, #431407 100%)',
    boxShadow: '0 0 0 2px rgba(180,83,9,0.4), 0 0 14px rgba(194,65,12,0.3), 0 0 28px rgba(120,53,15,0.15)',
    shimmerColor: 'rgba(254,215,170,0.6)',
    pulseColor: null,
    nameFrom: '#fdba74',
    nameTo: '#fcd34d',
    hasPulse: false,
  },
};

const SIZES = {
  sm: { outer: 'w-6 h-6', emoji: 'text-xs' },
  md: { outer: 'w-9 h-9', emoji: 'text-base' },
  lg: { outer: 'w-14 h-14', emoji: 'text-2xl' },
  xl: { outer: 'w-20 h-20', emoji: 'text-4xl' },
};

const NAME_SIZES = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
  xl: 'text-lg',
};

export default function UserBadge({ badge, size = 'md', showName = false, animate = false }) {
  if (!badge || !badge.tier) return null;

  const cfg = TIER_CONFIG[badge.tier] || TIER_CONFIG.bronze;
  const sz = SIZES[size] || SIZES.md;
  const nameSz = NAME_SIZES[size] || NAME_SIZES.md;

  const BadgeCircle = (
    <motion.div
      data-testid={`user-badge-${badge.tier}`}
      className={`${sz.outer} rounded-full flex items-center justify-center relative cursor-pointer select-none overflow-hidden flex-shrink-0`}
      style={{ background: cfg.bg, boxShadow: cfg.boxShadow }}
      initial={animate ? { scale: 0, rotate: -180 } : false}
      animate={animate ? { scale: 1, rotate: 0 } : false}
      transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.2 }}
      whileHover={{ scale: 1.12 }}
    >
      {/* Shimmer sweep */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: `linear-gradient(105deg, transparent 35%, ${cfg.shimmerColor} 50%, transparent 65%)`,
        }}
        animate={{ x: ['-100%', '250%'] }}
        transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3.5, ease: 'easeInOut' }}
      />

      {/* Pulse ring for premium tiers */}
      {cfg.hasPulse && (
        <motion.div
          className="absolute inset-[-3px] rounded-full pointer-events-none"
          animate={{
            boxShadow: [
              `0 0 0 0px ${cfg.pulseColor}`,
              `0 0 0 5px transparent`,
            ],
          }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay: 0.8 }}
        />
      )}

      <span
        className={`relative z-10 ${sz.emoji} leading-none`}
        style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.7))' }}
      >
        {badge.emoji}
      </span>
    </motion.div>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-2" data-testid="user-badge">
            {BadgeCircle}
            {showName && (
              <motion.span
                className={`font-semibold ${nameSz}`}
                style={{
                  background: `linear-gradient(90deg, ${cfg.nameFrom}, ${cfg.nameTo})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
                initial={animate ? { opacity: 0, x: -16 } : false}
                animate={animate ? { opacity: 1, x: 0 } : false}
                transition={{ delay: 0.5 }}
              >
                {badge.name}
              </motion.span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <div className="text-center px-1 py-0.5">
            <div className="font-semibold text-sm">{badge.emoji} {badge.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{badge.description}</div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
